type AuthErrorContext = 'email-sign-in' | 'google-sign-in' | 'password-reset' | 'sign-up'

function authErrorCode(error: unknown): string | null {
  if (!error || typeof error !== 'object' || !('code' in error)) return null
  return typeof error.code === 'string' ? error.code : null
}

export function isUnknownPasswordResetAccount(error: unknown): boolean {
  return authErrorCode(error) === 'auth/user-not-found'
}

export function authErrorMessage(error: unknown, context: AuthErrorContext): string {
  const code = authErrorCode(error)

  if (code === 'auth/network-request-failed') {
    return 'We could not reach the sign-in service. Check your connection and try again.'
  }

  if (code === 'auth/too-many-requests') {
    return 'There have been too many attempts. Please wait a moment before trying again.'
  }

  if (context === 'google-sign-in') {
    switch (code) {
      case 'auth/popup-closed-by-user':
      case 'auth/cancelled-popup-request':
        return 'Google sign-in was cancelled. You can try again whenever you are ready.'
      case 'auth/popup-blocked':
        return 'Your browser blocked the Google sign-in window. Allow popups for this site and try again.'
      case 'auth/account-exists-with-different-credential':
        return 'An account already uses this email. Sign in with your email and password instead.'
      case 'auth/unauthorized-domain':
        return 'Google sign-in is not available from this web address. Please contact One Day Dance.'
      case 'auth/operation-not-allowed':
        return 'Google sign-in is temporarily unavailable. Use your email and password instead.'
      default:
        return 'We could not complete Google sign-in. Please try again.'
    }
  }

  if (context === 'password-reset') {
    switch (code) {
      case 'auth/invalid-email':
        return 'Enter a valid email address.'
      case 'auth/operation-not-allowed':
        return 'Password recovery is temporarily unavailable. Please contact One Day Dance.'
      default:
        return 'We could not send the recovery email. Please try again.'
    }
  }

  if (context === 'sign-up') {
    switch (code) {
      case 'auth/email-already-in-use':
        return 'An account already uses this email. Sign in instead or reset your password.'
      case 'auth/invalid-email':
        return 'Enter a valid email address.'
      case 'auth/weak-password':
        return 'Choose a stronger password with at least 8 characters.'
      case 'auth/operation-not-allowed':
        return 'Account creation is temporarily unavailable. Please contact One Day Dance.'
      default:
        return 'We could not create your account. Please try again.'
    }
  }

  switch (code) {
    case 'auth/invalid-email':
      return 'Enter a valid email address.'
    case 'auth/invalid-credential':
    case 'auth/invalid-login-credentials':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
      return 'That email or password does not look right. Please try again.'
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact One Day Dance for help.'
    case 'auth/operation-not-allowed':
      return 'Email sign-in is temporarily unavailable. Please try Google instead.'
    default:
      return 'We could not sign you in. Please try again.'
  }
}
